// Frontend presentation config for clinic types.
// The set of keys mirrors backend/src/lib/clinic-type-presets.ts — keep in sync.
// This module is pure data + a resolver; no React, no fetching.

export const clinicTypes = [
  'med_spa',
  'dermatology',
  'physio',
  'gp',
  'wellness',
  'iv_clinic',
  'dental',
  'radiology',
  'other',
] as const;

export type ClinicType = (typeof clinicTypes)[number];

// Which noun the vertical uses for the people it serves. Med spas / wellness
// brands say "client"; clinical verticals say "patient". Stored as a semantic
// term (not a localized string) so the i18n layer can localize it.
export type PatientTerm = 'patient' | 'client';

export interface ClinicTypePresentation {
  /** Stable key. */
  clinicType: ClinicType;
  /** Human-facing label (English; localize later if needed). */
  label: string;
  /** Semantic term for served persons; resolved to a localized string by the UI. */
  patientTerm: PatientTerm;
}

export const clinicTypePresentation: Record<ClinicType, ClinicTypePresentation> = {
  med_spa: { clinicType: 'med_spa', label: 'Med Spa / Aesthetics', patientTerm: 'client' },
  wellness: { clinicType: 'wellness', label: 'Wellness / IV', patientTerm: 'client' },
  dermatology: { clinicType: 'dermatology', label: 'Dermatology', patientTerm: 'patient' },
  physio: { clinicType: 'physio', label: 'Physiotherapy / Rehab', patientTerm: 'patient' },
  gp: { clinicType: 'gp', label: 'GP / Primary Care', patientTerm: 'patient' },
  iv_clinic: { clinicType: 'iv_clinic', label: 'IV / Infusion', patientTerm: 'patient' },
  dental: { clinicType: 'dental', label: 'Dental', patientTerm: 'patient' },
  radiology: { clinicType: 'radiology', label: 'Radiology / Imaging', patientTerm: 'patient' },
  other: { clinicType: 'other', label: 'Other / General', patientTerm: 'patient' },
};

export function normalizeClinicType(input?: unknown): ClinicType | null {
  if (typeof input !== 'string') return null;
  const value = input.trim().toLowerCase().replace(/[\s-]+/g, '_');
  return (clinicTypes as readonly string[]).includes(value) ? (value as ClinicType) : null;
}

/** Resolve presentation config for a clinic type; unknown/null falls back to `other`. */
export function getClinicTypePresentation(input?: unknown): ClinicTypePresentation {
  const type = normalizeClinicType(input);
  return clinicTypePresentation[type ?? 'other'];
}
